import { useMantineColorScheme } from '@mantine/core';
import type { ExecutionError, ExecutionTrace, NodeRegistry, TraceLabels } from '@bett3r-dev/teselly-rule-editor';
import { RuleEditor } from '@bett3r-dev/teselly-rule-editor';
import nodeRegistryJson from '@bett3r-dev/teselly-rule-editor/registry';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { stringify } from 'yaml';

const nodeRegistry = nodeRegistryJson as unknown as NodeRegistry;

/**
 * Shape produced by the dry-run endpoint when a node fails. Matches
 * `CustomRulesDryRunReturnTypeSchema.error` (see ticket TV1-1496) — already
 * 1:1 with `ExecutionError`.
 */
export type DryRunErrorPayload = {
  nodeId?: string;
  nodeType?: string;
  message: string;
  code?: string;
  errorType: 'transient' | 'permanent';
};

/**
 * Shape stored on a historical `RuleExecutionFailed` readmodel document.
 * Matches `RuleExecutionErrorSchema` — uses `failedNodeId` / `failedNodeType`
 * instead of `nodeId` / `nodeType`. Consumed by the rule-execution-list trace
 * modal (TV1-1900) to render the failed-node banner + jump-to-failed-node for a
 * past firing.
 */
export type HistoricalExecutionErrorPayload = {
  message: string;
  code?: string;
  errorType: 'transient' | 'permanent';
  failedNodeId?: string;
  failedNodeType?: string;
};

export type RuleNodesViewProps = {
  dsl: unknown;
  executionTrace?: ExecutionTrace;
  /** Error information returned by the dry-run endpoint (node-level failure). */
  dryRunError?: DryRunErrorPayload;
  /**
   * Error information stored on a historical `RuleExecutionFailed` readmodel
   * document. When provided (and `dryRunError` is not), the `failedNodeId` /
   * `failedNodeType` keys are remapped onto the rule-editor's `ExecutionError`
   * shape so the banner + jump-to-failed-node render.
   */
  historicalExecutionError?: HistoricalExecutionErrorPayload;
  /** Height of the embedded editor surface. Defaults to `100%`. */
  height?: string | number;
};

/**
 * Presentational, read-only render of a rule as the dry-run node graph, with an
 * optional execution trace and failure overlay. Owns the `ExecutionError` remap
 * and `traceLabels` so every caller (dry-run modal + execution-list trace modal)
 * renders an identical graph. No modal chrome — embed inside any container.
 */
export const RuleNodesView: React.FC<RuleNodesViewProps> = ({
  dsl,
  executionTrace,
  dryRunError,
  historicalExecutionError,
  height = '100%'
}) => {
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation( 'Rules' );
  const dslString = typeof dsl === 'string' ? dsl : stringify( dsl );

  /**
   * Build the unified `ExecutionError` shape for the rule editor. Prefers
   * `dryRunError` (1:1 mapping) when both are present. For historical errors,
   * remap `failedNodeId` / `failedNodeType` to `nodeId` / `nodeType`.
   */
  const executionError = React.useMemo<ExecutionError | undefined>(() => {
    if ( dryRunError ) {
      return {
        ...( dryRunError.nodeId !== undefined ? { nodeId: dryRunError.nodeId } : {}),
        ...( dryRunError.nodeType !== undefined ? { nodeType: dryRunError.nodeType } : {}),
        ...( dryRunError.code !== undefined ? { code: dryRunError.code } : {}),
        message: dryRunError.message,
        errorType: dryRunError.errorType
      };
    }
    if ( historicalExecutionError ) {
      return {
        ...( historicalExecutionError.failedNodeId !== undefined ? { nodeId: historicalExecutionError.failedNodeId } : {}),
        ...( historicalExecutionError.failedNodeType !== undefined ? { nodeType: historicalExecutionError.failedNodeType } : {}),
        ...( historicalExecutionError.code !== undefined ? { code: historicalExecutionError.code } : {}),
        message: historicalExecutionError.message,
        errorType: historicalExecutionError.errorType
      };
    }
    return undefined;
  }, [ dryRunError, historicalExecutionError ]);

  const traceLabels = React.useMemo<TraceLabels>(() => ({
    fullValueNotStored: t( 'trace_full_value_not_stored', 'Valor completo no almacenado en la traza.' ),
    noNodesExecuted: t( 'trace_no_nodes_executed', 'Sin nodos ejecutados' ),
    executionFailedBanner: t( 'trace_execution_failed_banner', 'Ejecución de la regla falló en el nodo {{nodeId}}' ),
    jumpToFailedNode: t( 'trace_jump_to_failed_node', 'Ir al nodo que falló' ),
    nodeFailedBadge: t( 'trace_node_failed_badge', 'Error' ),
    errorTypeTransient: t( 'trace_error_type_transient', 'transitorio' ),
    errorTypePermanent: t( 'trace_error_type_permanent', 'permanente' ),
    errorMessagePrefix: t( 'trace_error_message_prefix', 'Mensaje de error' )
  }), [t]);

  return (
    <div style={{ width: '100%', height }}>
      <RuleEditor
        dsl={dslString}
        nodeRegistry={nodeRegistry}
        readOnly
        theme={colorScheme === 'dark' ? 'dark' : 'light'}
        executionTrace={executionTrace}
        executionError={executionError}
        traceLabels={traceLabels}
        showEdgeLabelsByDefault
      />
    </div>
  );
};

export default RuleNodesView;
